import React from 'react';

import { Button, Card, Typography } from '@mui/material';

import { Location } from '@kamernet/core/ApiClient/Kamernet';
import { AppServicesContext } from '@kamernet/core/AppServices';
import { useIntl } from '@kamernet/core/Intl';
import { PageLayout } from '@kamernet/core/PageLayout';
import { ROUTES } from '@kamernet/core/Routing';
import { NavLink } from '@kamernet/core/Routing/NavLink';
import LogoIcon from '@kamernet/icons/Logo.svg';
import { CommonStyles } from '@kamernet/styles/CommonStyles';

import { LocationAutocomplete } from '@kamernet/modules/Listing/components/LocationAutocomplete';

import styles from './CreateAdvert.module.css';

export interface CreateAdvertProps {
  averageRentoutDayInNL: number;
  activeTenantCountInNL: number;
}

export const CreateAdvert = ({
  averageRentoutDayInNL,
  activeTenantCountInNL,
}: CreateAdvertProps) => {
  const { T, locale } = useIntl();
  const { apiClient } = AppServicesContext.useContext();

  const averageRentoutDayInNLText = React.useMemo(() => {
    return T(
      'LBL_HOME_SUPPLY_H2_DYNAMIC',
      T('LBL_NETHERLANDS'),
      averageRentoutDayInNL,
    );
  }, [T, averageRentoutDayInNL]);

  const activeTenantCountInNLText = React.useMemo(() => {
    return T(
      'LBL_HOME_SUPPLY_TOP_USERS_DYNAMIC',
      activeTenantCountInNL,
      T('LBL_NETHERLANDS'),
    );
  }, [T, activeTenantCountInNL]);

  const abortControllerRef = React.useRef<AbortController | null>(null);
  const [location, setLocation] = React.useState<Location | null>(null);

  const [averageRentoutDayText, setAverageRentoutDayText] =
    React.useState<string>(averageRentoutDayInNLText);

  const [activeTenantCountText, setActiveTenantCountText] =
    React.useState<string>(activeTenantCountInNLText);

  const onLocationChange = React.useCallback(
    async (value: Location | null) => {
      setLocation(value);
      abortControllerRef.current?.abort();
      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      if (value != null) {
        const [averageRentoutDayResponse, activeTenantCountResponse] =
          await Promise.all([
            apiClient.other.averageRentoutDay(value.cityName, {
              signal: abortController.signal,
            }),
            apiClient.other.activeTenantCount(value.cityName, {
              signal: abortController.signal,
            }),
          ]);

        const activeTenantCount = activeTenantCountResponse.data;

        if (activeTenantCount == null || activeTenantCount < 50) {
          setAverageRentoutDayText(averageRentoutDayInNLText);
          setActiveTenantCountText(activeTenantCountInNLText);
        } else {
          setAverageRentoutDayText(
            T(
              'LBL_HOME_SUPPLY_H2_DYNAMIC',
              value.cityName,
              averageRentoutDayResponse.data,
            ),
          );

          setActiveTenantCountText(
            T(
              'LBL_HOME_SUPPLY_TOP_USERS_DYNAMIC',
              activeTenantCount,
              value.cityName,
            ),
          );
        }
      } else {
        setAverageRentoutDayText(averageRentoutDayInNLText);
        setActiveTenantCountText(activeTenantCountInNLText);
      }
    },
    [T, activeTenantCountInNLText, apiClient.other, averageRentoutDayInNLText],
  );

  return (
    <PageLayout.SectionContainer
      className={styles.root}
      containerClassName={styles.sectionContainer}
    >
      <div>
        <Typography
          variant="h1"
          color="common.white"
          className={CommonStyles.padding_bottom_1}
        >
          {T('LBL_GLOBAL_SUPPLY_FOOTER_RENT_OUT_ROOM_TITLE')}
        </Typography>
        <Typography
          variant="h3"
          color="common.white"
          className={CommonStyles.padding_bottom_1}
        >
          {T('LBL_SUPPLY_HOMEPAGE_ABOVE_LOGO', T('LBL_ROOM_TILE_ROOM'))}
        </Typography>
        <LogoIcon className={styles.logo} viewBox="0 0 82.88 15.35" />
      </div>
      <Card elevation={0} className={styles.formCard}>
        <form className={styles.form}>
          <Typography variant="h3" className={CommonStyles.padding_bottom_2}>
            {averageRentoutDayText}
          </Typography>
          <Typography
            variant="h6"
            color="secondary.light"
            className={CommonStyles.padding_bottom_3}
          >
            {T('LBL_HOME_SUPPLY_TOP_SUBTITLE')}
          </Typography>
          <LocationAutocomplete value={location} onChange={onLocationChange} />
          <Typography variant="body2" className={CommonStyles.padding_top_3}>
            {activeTenantCountText}
          </Typography>
          <Button
            component={NavLink.Anchor}
            href={ROUTES.LCFStart.url({}, locale)}
            fullWidth
            className={CommonStyles.margin_top_2}
            size="large"
            variant="contained"
            color="tertiary"
          >
            {T('LBL_HOME_SUPPLY_TOP_CTA')}
          </Button>
        </form>
      </Card>
    </PageLayout.SectionContainer>
  );
};
